/**
 * Visitor Beacon Service
 * 
 * Fires exactly ONE visitor notification per session to the backend.
 * No click tracking. No navigation tracking. No ongoing telemetry.
 */

import { API_BASE_URL } from './config';

interface VisitorData {
    sessionId: string;
    device: {
        type: string;
        os: string;
        osVersion: string;
        screenWidth: number;
        screenHeight: number;
    };
    browser: {
        name: string;
        version: string;
    };
    language: string;
    timezone: string;
    referrer?: string;
    
    // Extended fields (optional, backward-compatible)
    visitorType?: 'new' | 'returning';
    landingPageUrl?: string;
    streamId?: string;
    requestRate?: number;
    // Note: sessionDuration, watchDuration, playResult are sent via endSession()
}

/**
 * Extended session data for end-of-session reporting
 */
interface SessionEndData {
    sessionId: string;
    sessionDuration?: number;
    watchDuration?: number;
    playResult?: 'completed' | 'dropped' | 'error';
    vpnProxyFlag?: boolean;
    requestRate?: number;
}

class VisitorBeacon {
    private sessionId: string;
    private hasNotified = false;
    private apiUrl: string;
    private sessionStartTime: number;
    private requestCount = 0;
    private landingPageUrl: string;

    // Known bot/crawler user agent patterns
    private static readonly BOT_PATTERNS = [
        /googlebot/i,
        /bingbot/i,
        /slurp/i,           // Yahoo
        /duckduckbot/i,
        /baiduspider/i,
        /yandexbot/i,
        /sogou/i,
        /exabot/i,
        /facebot/i,         // Facebook
        /ia_archiver/i,     // Alexa
        /mj12bot/i,
        /ahrefsbot/i,
        /semrushbot/i,
        /dotbot/i,
        /rogerbot/i,
        /twitterbot/i,
        /linkedinbot/i,
        /pinterest/i,
        /whatsapp/i,
        /telegrambot/i,
        /discordbot/i,
        /slackbot/i,
        /applebot/i,
        /headlesschrome/i,  // Headless browsers
        /phantomjs/i,
        /puppeteer/i,
        /playwright/i,
        /crawl/i,
        /spider/i,
        /bot\b/i,           // Generic "bot" suffix
        /lighthouse/i,      // Google Lighthouse
        /pagespeed/i,
        /gtmetrix/i,
        /pingdom/i,
        /uptimerobot/i,
    ];

    constructor() {
        this.apiUrl = API_BASE_URL;
        this.sessionId = this.getOrCreateSessionId();
        this.sessionStartTime = Date.now();
        this.landingPageUrl = window.location.href;
        
        // Check if we've already notified for this session
        this.hasNotified = sessionStorage.getItem('visitor_notified') === 'true';
        
        // Track request count for this session
        const storedCount = sessionStorage.getItem('visitor_request_count');
        this.requestCount = storedCount ? parseInt(storedCount, 10) : 0;
    }

    /**
     * Check if the current user agent appears to be a bot/crawler
     */
    private isBot(): boolean {
        try {
            const ua = navigator.userAgent;
            if (!ua) return true; // No user agent = suspicious
            
            return VisitorBeacon.BOT_PATTERNS.some(pattern => pattern.test(ua));
        } catch {
            return false;
        }
    }

    /**
     * Check if we're in a genuine browser environment
     */
    private isBrowserEnvironment(): boolean {
        try {
            // Must have window and document
            if (typeof window === 'undefined' || typeof document === 'undefined') {
                return false;
            }
            
            // Skip if page is hidden (includes prerendering in modern browsers)
            if (document.hidden) {
                return false;
            }
            
            // Must have navigator with userAgent
            if (!navigator?.userAgent) {
                return false;
            }
            
            // Check for headless browser indicators
            // @ts-ignore - webdriver is a non-standard property
            if (navigator.webdriver) {
                return false;
            }
            
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Notify backend of visitor arrival - fires exactly once per session
     * Only fires for genuine browser visitors (not bots/crawlers)
     */
    async notify(): Promise<void> {
        // Skip if already notified this session
        if (this.hasNotified) {
            console.log('[Visitor] Already notified this session');
            return;
        }

        // Skip if not a genuine browser environment
        if (!this.isBrowserEnvironment()) {
            console.log('[Visitor] Skipping notification - not a browser environment');
            return;
        }

        // Skip if detected as a bot/crawler
        if (this.isBot()) {
            console.log('[Visitor] Skipping notification - detected as bot/crawler');
            return;
        }

        try {
            const data = this.collectVisitorData();
            
            const response = await fetch(`${this.apiUrl}/api/alerts/visitor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            if (response.ok || response.status === 204) {
                // Mark as notified regardless of 200/204
                this.hasNotified = true;
                sessionStorage.setItem('visitor_notified', 'true');
                console.log('[Visitor] Beacon sent successfully');
            } else {
                console.warn('[Visitor] Beacon failed:', response.status);
            }
        } catch (error) {
            // Silent failure - don't break the user experience
            console.warn('[Visitor] Beacon error:', error);
        }
    }

    /**
     * Increment request count (call this on significant user actions)
     */
    incrementRequestCount(): void {
        this.requestCount++;
        try {
            sessionStorage.setItem('visitor_request_count', this.requestCount.toString());
        } catch {
            // Fail silently
        }
    }

    /**
     * Get current session duration in seconds
     */
    getSessionDuration(): number {
        return Math.floor((Date.now() - this.sessionStartTime) / 1000);
    }

    /**
     * Send session end data (optional, call before page unload if needed)
     * This is fire-and-forget using sendBeacon for reliability
     */
    endSession(additionalData?: Partial<SessionEndData>): void {
        try {
            const endData: SessionEndData = {
                sessionId: this.sessionId,
                sessionDuration: this.getSessionDuration(),
                requestRate: this.requestCount,
                ...additionalData,
            };

            // Use sendBeacon for reliability on page unload
            const blob = new Blob([JSON.stringify(endData)], { type: 'application/json' });
            navigator.sendBeacon(`${this.apiUrl}/api/alerts/visitor/end`, blob);
        } catch {
            // Fail silently - session end tracking is optional
        }
    }

    /**
     * Collect minimal visitor context
     */
    private collectVisitorData(): VisitorData {
        const ua = navigator.userAgent;
        
        // Parse browser
        let browserName = 'Unknown';
        let browserVersion = 'Unknown';
        
        if (ua.includes('Chrome') && !ua.includes('Edg') && !ua.includes('OPR')) {
            browserName = 'Chrome';
            const match = ua.match(/Chrome\/(\d+\.?\d*)/);
            browserVersion = match ? match[1] : 'Unknown';
        } else if (ua.includes('Firefox')) {
            browserName = 'Firefox';
            const match = ua.match(/Firefox\/(\d+\.?\d*)/);
            browserVersion = match ? match[1] : 'Unknown';
        } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
            browserName = 'Safari';
            const match = ua.match(/Version\/(\d+\.?\d*)/);
            browserVersion = match ? match[1] : 'Unknown';
        } else if (ua.includes('Edg')) {
            browserName = 'Edge';
            const match = ua.match(/Edg\/(\d+\.?\d*)/);
            browserVersion = match ? match[1] : 'Unknown';
        }
        
        // Parse OS
        let osName = 'Unknown';
        let osVersion = 'Unknown';
        
        if (ua.includes('Windows')) {
            osName = 'Windows';
            if (ua.includes('Windows NT 10.0')) osVersion = '10/11';
            else if (ua.includes('Windows NT 6.3')) osVersion = '8.1';
            else if (ua.includes('Windows NT 6.1')) osVersion = '7';
        } else if (ua.includes('Mac OS X')) {
            osName = 'macOS';
            const match = ua.match(/Mac OS X (\d+[._]\d+)/);
            osVersion = match ? match[1].replace('_', '.') : 'Unknown';
        } else if (ua.includes('Android')) {
            osName = 'Android';
            const match = ua.match(/Android (\d+\.?\d*)/);
            osVersion = match ? match[1] : 'Unknown';
        } else if (ua.includes('iPhone') || ua.includes('iPad')) {
            osName = 'iOS';
            const match = ua.match(/OS (\d+[._]\d+)/);
            osVersion = match ? match[1].replace('_', '.') : 'Unknown';
        } else if (ua.includes('Linux')) {
            osName = 'Linux';
        }
        
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
        
        // Determine visitor type (new vs returning)
        const visitorType = this.determineVisitorType();
        
        return {
            sessionId: this.sessionId,
            device: {
                type: isMobile ? 'mobile' : 'desktop',
                os: osName,
                osVersion: osVersion,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
            },
            browser: {
                name: browserName,
                version: browserVersion,
            },
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            referrer: document.referrer || undefined,
            
            // Extended fields
            visitorType,
            landingPageUrl: this.landingPageUrl,
            streamId: this.extractStreamId(),
            requestRate: this.requestCount,
        };
    }

    /**
     * Determine if visitor is new or returning
     * Uses localStorage to persist across sessions
     */
    private determineVisitorType(): 'new' | 'returning' {
        try {
            const hasVisitedBefore = localStorage.getItem('visitor_has_visited');
            if (hasVisitedBefore === 'true') {
                return 'returning';
            }
            // Mark as visited for future sessions
            localStorage.setItem('visitor_has_visited', 'true');
            return 'new';
        } catch {
            // If localStorage is unavailable, assume new
            return 'new';
        }
    }

    /**
     * Extract stream/link ID from URL if applicable
     */
    private extractStreamId(): string | undefined {
        try {
            const url = new URL(window.location.href);
            // Check common patterns for stream/link IDs
            return url.searchParams.get('stream') ||
                   url.searchParams.get('link') ||
                   url.searchParams.get('ref') ||
                   url.searchParams.get('id') ||
                   undefined;
        } catch {
            return undefined;
        }
    }

    /**
     * Get or create a unique session ID
     */
    private getOrCreateSessionId(): string {
        let sessionId = sessionStorage.getItem('visitor_session_id');
        if (!sessionId) {
            sessionId = 'v_' + crypto.randomUUID();
            sessionStorage.setItem('visitor_session_id', sessionId);
        }
        return sessionId;
    }

    /**
     * Get the current session ID (for external use)
     */
    getSessionId(): string {
        return this.sessionId;
    }
}

// Export singleton
export const visitorBeacon = new VisitorBeacon();

